import { useMemo } from 'react';
import {
  Archive,
  Bell,
  CircleDot,
  Clock,
  GitBranch,
  Link2,
  PencilLine,
  Shapes,
  Tag,
  Tags,
  User,
  Users,
  Workflow,
} from 'lucide-react';
import { FilterBar } from '@/components/filters/FilterBar';
import {
  FILTER_ALL_VALUE,
  type FilterDefinition,
  type FilterOption,
} from '@/components/filters/filter-types';
import { useUsers } from '@/hooks/auth/useUsers';
import { useAgentGroups } from '@/hooks/agents/useAgentGroups';
import { useAgentLabels } from '@/hooks/agents/useAgentLabels';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePlugins } from '@/hooks/config/usePlugins';
import type { AgentFiltersResult, AgentMarkFilter } from '@/hooks/agents/useAgentFilters';
import { AGENT_MARKS } from '@/lib/types';
import { AGENT_MARK_CONFIG } from '@/lib/agent-marks';
import type { Agent } from '@/lib/types';
import { getUserDisplayName } from '@/lib/agent-utils';
import { normalizeRepoSlug } from '@reflex/ui/lib/repo-slug';
import {
  ASSOCIATION_KIND_PULL_REQUEST,
  ORIGINATOR_KIND_AGENT,
  ORIGINATOR_KIND_FLOW,
  isFlowOriginator,
  matchesOriginatorKind,
  getGitRepoAttachment,
} from '@reflex/shared';

const ALL_VALUE = FILTER_ALL_VALUE;
/** Filter value for "carries any mark at all", as opposed to a specific one. */
const MARKED_VALUE = 'marked';

// Built-in originator kinds the host always knows. Plugin-contributed kinds
// (e.g. Jira `jrr`) are merged in from the plugin registry at render time.
const BUILTIN_ORIGINATOR_KINDS: FilterOption[] = [
  { value: ORIGINATOR_KIND_AGENT, label: 'Agent' },
  { value: ORIGINATOR_KIND_FLOW, label: 'Automation' },
];

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'error', label: 'Error' },
  { value: 'stopped', label: 'Stopped' },
];

/** A flow run id is a bare UUID; show a short, recognizable prefix of it. */
function shortFlowId(flowRunId: string): string {
  return flowRunId.slice(0, 8);
}

// Triage marks, plus a "carries any mark" entry that turns the list into the
// triage inbox. Labels come from the same config the row icons and quick
// actions use.
const MARK_OPTIONS: FilterOption[] = [
  { value: MARKED_VALUE, label: 'Marked' },
  ...AGENT_MARKS.map((mark) => ({ value: mark, label: AGENT_MARK_CONFIG[mark].label })),
];

const TIME_RANGE_OPTIONS: FilterOption[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
];

/** A single on/off facet: narrow to runs with finished-turn changes you have not looked at. */
const UNREAD_OPTIONS: FilterOption[] = [{ value: 'unread', label: 'Unread only' }];

const SHOW_OPTIONS: FilterOption[] = [{ value: 'drafts', label: 'Drafts only' }];

/**
 * A single on/off facet: narrow to archived runs. Without it the archived ones
 * are only reachable through the section at the very bottom of the sidebar,
 * which means scrolling past — or collapsing — every live session first.
 */
const ARCHIVED_OPTIONS: FilterOption[] = [{ value: 'archived', label: 'Archived only' }];

interface AgentFilterBarProps {
  filtersResult: AgentFiltersResult;
  /** All agents (before client-side filtering) for deriving repo options */
  agents: Agent[];
  className?: string;
}

export function AgentFilterBar({ filtersResult, agents, className }: AgentFilterBarProps) {
  const {
    filters,
    currentUserId,
    setUserId,
    setStatus,
    setMark,
    setRepoFullName,
    setTimeRange,
    setQuery,
    setDraftsOnly,
    setOriginatorKind,
    setFlowOriginatorId,
    setAssociationKind,
    setGroupId,
    setLabelId,
    setUnreadOnly,
    setArchivedOnly,
    clearFilters,
  } = filtersResult;
  const { data: users = [] } = useUsers();
  const { data: groups = [] } = useAgentGroups();
  const { data: labels = [] } = useAgentLabels();
  const { user } = useAuth();
  const { originators, associationChips } = usePlugins();

  // Originator/type options come from a plugin-contributed registry, not from
  // the loaded data: the host knows Agent + Flow, and each plugin declares the
  // kinds it can launch (e.g. Jira). Keyed by id prefix / built-in kind name.
  const originatorOptions = useMemo<FilterOption[]>(() => {
    const allKinds: FilterOption[] = [...BUILTIN_ORIGINATOR_KINDS];
    for (const o of originators.values()) {
      allKinds.push({ value: o.idPrefix, label: o.label });
    }
    return allKinds.map((opt) => ({
      ...opt,
      count: agents.filter((a) => matchesOriginatorKind(a.originator, opt.value)).length,
    }));
  }, [originators, agents]);

  // Association kinds come from the same registry the chips render through:
  // the core Pull request kind plus every plugin-registered chip's kind and
  // label. Filtering keeps agents linked to at least one item of the kind.
  const associationKindOptions = useMemo<FilterOption[]>(() => {
    const opts: FilterOption[] = [{ value: ASSOCIATION_KIND_PULL_REQUEST, label: 'Pull request' }];
    for (const chip of associationChips.values()) {
      opts.push({ value: chip.kind, label: chip.label });
    }
    return opts;
  }, [associationChips]);

  // Groups come from the org's group list, not from the loaded agents: a
  // group you have scrolled past (or an empty one) is still worth filtering
  // to. The count rides in the label so picking one is an informed choice.
  const groupOptions = useMemo<FilterOption[]>(
    () =>
      groups.map((group) => ({ value: group.id, label: `${group.name} (${group.memberCount})` })),
    [groups],
  );

  // Labels come from the org catalog for the same reason groups do, and the
  // count says how much of the list a label would actually leave you with.
  const labelOptions = useMemo<FilterOption[]>(
    () =>
      labels.map((label) => ({ value: label.id, label: `${label.name} (${label.memberCount})` })),
    [labels],
  );

  // "Just mine" is the owner filter people reach for most, so the signed-in
  // user leads the list and reads as "Me" instead of sitting somewhere in an
  // org roster under their own name. The name stays in the label so the value
  // search and `@owner:` in the search box still find you by name; everyone
  // else is sorted by it, since the roster arrives in no useful order.
  //
  // Owner scoping keeps the list to your own sessions, org-shared ones, and the
  // ones granted to you, so the others come from the owners actually in view
  // rather than the whole roster: a colleague whose sessions you cannot see
  // could only ever filter the list down to nothing. "Me" is unconditional —
  // narrowing a mixed list to your own runs is worth a facet on its own.
  const ownerOptions = useMemo<FilterOption[]>(() => {
    // Falls back to the session user, since the roster is a separate request:
    // "Me" should not blink into existence once that one lands.
    const me =
      users.find((u) => u.id === currentUserId) ??
      (user && user.id === currentUserId ? user : null);

    // Count agents per userId for the trailing count column.
    const countByUser = new Map<string, number>();
    for (const a of agents) {
      if (a.userId) countByUser.set(a.userId, (countByUser.get(a.userId) ?? 0) + 1);
    }

    const opts: FilterOption[] = [];
    if (me) {
      opts.push({
        value: me.id,
        label: `Me (${getUserDisplayName(me)})`,
        count: countByUser.get(me.id) ?? 0,
      });
    }
    // The applied owner is in view by construction — it is what narrowed the
    // list — but keep it listed even when that left no runs, so the chip reads
    // as a name and picking someone else stays one click away.
    const inView = new Set<string>(filters.userId ? [filters.userId] : []);
    for (const a of agents) if (a.userId) inView.add(a.userId);
    const others = users
      .filter((u) => u.id !== currentUserId && inView.has(u.id))
      .sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));
    for (const u of others) {
      opts.push({ value: u.id, label: getUserDisplayName(u), count: countByUser.get(u.id) ?? 0 });
    }
    return opts;
  }, [users, currentUserId, user, agents, filters.userId]);

  const repoOptions = useMemo<FilterOption[]>(() => {
    const countByRepo = new Map<string, number>();
    for (const a of agents) {
      const repoSlug = getGitRepoAttachment(a)?.repoSlug;
      if (repoSlug) countByRepo.set(repoSlug, (countByRepo.get(repoSlug) ?? 0) + 1);
    }
    return Array.from(countByRepo.keys())
      .sort()
      .map((slug) => ({
        value: slug,
        label: normalizeRepoSlug(slug),
        count: countByRepo.get(slug) ?? 0,
      }));
  }, [agents]);

  // Distinct flow runs present in the current agent set, so the flow picker
  // only ever offers flows you actually have runs for.
  const flowOptions = useMemo<FilterOption[]>(() => {
    const runIds = new Set<string>();
    for (const a of agents) {
      if (isFlowOriginator(a.originator)) runIds.add(a.originator!);
    }
    const opts: FilterOption[] = [];
    for (const runId of Array.from(runIds).sort()) {
      opts.push({ value: runId, label: `Automation ${shortFlowId(runId)}` });
    }
    return opts;
  }, [agents]);

  const queryValue = filters.query ?? '';

  const filterDefinitions = useMemo<FilterDefinition[]>(() => {
    const definitions: FilterDefinition[] = [
      {
        id: 'type',
        label: 'Type',
        icon: Shapes,
        value: filters.originatorKind ?? ALL_VALUE,
        options: originatorOptions,
        onValueChange: (v) => setOriginatorKind(v === ALL_VALUE ? null : v),
        searchPlaceholder: 'Search types…',
      },
    ];

    // The specific-flow facet only makes sense once the list is already down to
    // flow-launched runs, and only when a flow run is actually in view.
    if (filters.originatorKind === ORIGINATOR_KIND_FLOW && flowOptions.length > 0) {
      definitions.push({
        id: 'flow',
        label: 'Automation',
        icon: Workflow,
        value: filters.flowOriginatorId ?? ALL_VALUE,
        options: flowOptions,
        onValueChange: (v) => setFlowOriginatorId(v === ALL_VALUE ? null : v),
        searchPlaceholder: 'Search automations…',
      });
    }

    definitions.push({
      id: 'owner',
      label: 'Owner',
      icon: User,
      value: filters.userId ?? ALL_VALUE,
      options: ownerOptions,
      onValueChange: (v) => setUserId(v === ALL_VALUE ? null : v),
      searchPlaceholder: 'Search people…',
      withSearch: true,
    });

    definitions.push(
      {
        id: 'status',
        label: 'Status',
        icon: CircleDot,
        value: filters.status ?? ALL_VALUE,
        options: STATUS_OPTIONS,
        onValueChange: (v) =>
          setStatus(v === ALL_VALUE ? null : (v as 'active' | 'completed' | 'error' | 'stopped')),
        searchPlaceholder: 'Search statuses…',
      },
      {
        id: 'mark',
        label: 'Mark',
        icon: Tag,
        value: filters.mark ?? ALL_VALUE,
        options: MARK_OPTIONS,
        onValueChange: (v) => setMark(v === ALL_VALUE ? null : (v as AgentMarkFilter)),
        searchPlaceholder: 'Search marks…',
      },
      {
        id: 'time',
        label: 'Time',
        icon: Clock,
        value: filters.timeRange ?? ALL_VALUE,
        options: TIME_RANGE_OPTIONS,
        onValueChange: (v) =>
          setTimeRange(v === ALL_VALUE ? null : (v as 'today' | 'week' | 'month')),
        searchPlaceholder: 'Search time range…',
      },
      {
        id: 'linked-to',
        label: 'Linked to',
        icon: Link2,
        value: filters.associationKind ?? ALL_VALUE,
        options: associationKindOptions,
        onValueChange: (v) => setAssociationKind(v === ALL_VALUE ? null : v),
        searchPlaceholder: 'Search kinds…',
      },
    );

    // One repo across every run means the facet can only ever be a no-op.
    if (repoOptions.length > 1) {
      definitions.push({
        id: 'repo',
        label: 'Repo',
        icon: GitBranch,
        value: filters.repoSlug ?? ALL_VALUE,
        options: repoOptions,
        onValueChange: (v) => setRepoFullName(v === ALL_VALUE ? null : v),
        searchPlaceholder: 'Search repos…',
        withSearch: true,
      });
    }

    if (labelOptions.length > 0) {
      definitions.push({
        id: 'label',
        label: 'Tags',
        icon: Tags,
        value: filters.labelId ?? ALL_VALUE,
        options: labelOptions,
        onValueChange: (v) => setLabelId(v === ALL_VALUE ? null : v),
        searchPlaceholder: 'Search tags…',
        withSearch: true,
      });
    }

    if (groupOptions.length > 0) {
      definitions.push({
        id: 'group',
        label: 'Group',
        icon: Users,
        value: filters.groupId ?? ALL_VALUE,
        options: groupOptions,
        onValueChange: (v) => setGroupId(v === ALL_VALUE ? null : v),
        searchPlaceholder: 'Search groups…',
      });
    }

    definitions.push({
      id: 'unread',
      label: 'Unread',
      icon: Bell,
      value: filters.unreadOnly ? 'unread' : ALL_VALUE,
      options: UNREAD_OPTIONS,
      onValueChange: (v) => setUnreadOnly(v === 'unread'),
      searchPlaceholder: 'Search sessions…',
      quickToggle: true,
    });

    definitions.push({
      id: 'show',
      label: 'Drafts',
      icon: PencilLine,
      value: filters.draftsOnly ? 'drafts' : ALL_VALUE,
      options: SHOW_OPTIONS,
      onValueChange: (v) => setDraftsOnly(v === 'drafts'),
      searchPlaceholder: 'Search sessions…',
      quickToggle: true,
    });

    definitions.push({
      id: 'archived',
      label: 'Archived',
      icon: Archive,
      value: filters.archivedOnly ? 'archived' : ALL_VALUE,
      options: ARCHIVED_OPTIONS,
      onValueChange: (v) => setArchivedOnly(v === 'archived'),
      searchPlaceholder: 'Search sessions…',
      quickToggle: true,
    });

    return definitions;
  }, [
    filters,
    originatorOptions,
    flowOptions,
    ownerOptions,
    associationKindOptions,
    labelOptions,
    groupOptions,
    repoOptions,
    setOriginatorKind,
    setFlowOriginatorId,
    setUserId,
    setStatus,
    setMark,
    setTimeRange,
    setLabelId,
    setGroupId,
    setAssociationKind,
    setRepoFullName,
    setUnreadOnly,
    setArchivedOnly,
    setDraftsOnly,
  ]);

  return (
    <FilterBar
      className={className}
      filters={filterDefinitions}
      onClear={clearFilters}
      search={{
        value: queryValue,
        onValueChange: (v) => setQuery(v.length > 0 ? v : null),
        // Short enough to survive the dock variant, where the input clips
        // anything longer mid-word ("Search runs, rep"). The list itself says
        // what is being searched; `@` in the input still surfaces the facets.
        placeholder: 'Search…',
        ariaLabel: 'Search agents',
      }}
    />
  );
}
